import { memo, useEffect, useRef } from 'react';
import { InView } from 'react-intersection-observer';
import { CountryCodes } from '@bridebook/toolbox/src/gazetteer';
import { styles } from './embedded-map.style';

const DEFAULT_HEIGHT = 228;
const DEFAULT_ZOOM = 13;
const DEFAULT_BORDER_RADIUS = 8;

interface IProps {
  supplierName: string;
  supplierCity: string;
  supplierPostalCode?: string;
  supplierCountry: CountryCodes;
  onMapInView?: () => void;
  onMouseUp?: () => void;
  zoom?: number;
  borderRadius?: number;
  height?: number;
}

const EmbeddedMap = ({
  supplierName,
  supplierCity,
  supplierCountry,
  supplierPostalCode,
  onMouseUp,
  onMapInView,
  borderRadius = DEFAULT_BORDER_RADIUS,
  zoom = DEFAULT_ZOOM,
  height = DEFAULT_HEIGHT,
}: IProps) => {
  const mouseUpTriggered = useRef(false);

  useEffect(() => {
    // This listener is needed to somehow check the iframe's interaction
    const listener = () => {
      if (
        onMouseUp &&
        !mouseUpTriggered.current &&
        document.activeElement === document.getElementById('embedded-map')
      ) {
        onMouseUp();
        mouseUpTriggered.current = true;
      }
    };
    window.addEventListener('blur', listener);
    return () => window.removeEventListener('blur', listener);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mouseUpTriggered]);

  const query = [supplierName, supplierCity, supplierPostalCode, supplierCountry]
    .filter(Boolean)
    .join(', ');

  const handleInView = (inView: boolean) => inView && onMapInView?.();

  const mapSrc = `https://www.google.com/maps/embed/v1/place?key=${
    process.env.NEXT_PUBLIC_FIREBASE_API_KEY
  }&q=${encodeURIComponent(query)}&zoom=${zoom}`;

  return (
    <InView
      threshold={1}
      delay={1500}
      as="div"
      onChange={(inView) => handleInView(inView)}
      triggerOnce>
      <iframe
        id="embedded-map"
        width="100%"
        height={height}
        loading="lazy"
        style={styles.wrapper(borderRadius)}
        allowFullScreen
        referrerPolicy="no-referrer-when-downgrade"
        src={mapSrc}></iframe>
    </InView>
  );
};

export default memo(EmbeddedMap);
